/**
 * effects.ts — Akili Brain: Visual & Audio Effects
 * ═══════════════════════════════════════════════════════════════════════════
 * FAB particle canvas system (unchanged).
 * Brainwave dual sine-wave canvas animation (new — driven by sentiment).
 * Ambient soundscape loader/unloader (new — Pixabay CC0 loop).
 *
 * All functions are safe to call multiple times; each guards against
 * duplicate initialisation and stale RAF handles internally.
 *
 * LAZY-LOAD REFACTOR:
 *   Previously this module held a static import of `setSoundscapeMode` from
 *   `./avatar-3d`, which forced Three.js + Loom3 (~700 kB minified) into the
 *   synchronous boot path even on pages where the 3D avatar never renders.
 *
 *   Fix: the static import is removed.  main.ts calls `registerAvatarSoundscape`
 *   after the avatar module has been lazy-loaded, supplying the real function.
 *   Until that registration happens the soundscape calls are no-ops — safe
 *   because the avatar canvas cannot yet be visible before init() resolves.
 */

import { state } from './state';

// ─────────────────────────────────────────────────────────────────────────────
// AVATAR SOUNDSCAPE BRIDGE
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Populated by `registerAvatarSoundscape()` once `./avatar-3d` has been
 * lazy-loaded.  Until then, soundscape start/stop silently skip the avatar
 * notification (the 3D canvas hasn't mounted yet so there is nothing to notify).
 */
let _avatarSoundscapeFn: ((active: boolean) => void) | null = null;

/**
 * Called by main.ts after `import('./avatar-3d')` resolves.
 * Supplies the real `setSoundscapeMode` function from the lazy chunk so
 * subsequent `startSoundscape` / `stopSoundscape` calls can reach the avatar.
 *
 * @param fn  `setSoundscapeMode` from avatar-3d.ts
 */
export function registerAvatarSoundscape(fn: (active: boolean) => void): void {
  _avatarSoundscapeFn = fn;
}

// ─────────────────────────────────────────────────────────────────────────────
// FAB PARTICLE SYSTEM (original, unchanged)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Initialises the particle canvas on the FAB and begins the animation loop.
 * Safe to call multiple times — exits early if canvas already present.
 */
export function initFabParticles() {
  const fab = document.getElementById('ai-fab');
  if (!fab || fab.querySelector('.fab-canvas')) return;

  const canvas = document.createElement('canvas');
  canvas.className = 'fab-canvas';
  const sz = 68;
  canvas.width  = sz;
  canvas.height = sz;
  fab.insertBefore(canvas, fab.firstChild);

  state.fabCanvas    = canvas;
  state.fabCtx       = canvas.getContext('2d');
  state.fabParticles = [];

  for (let i = 0; i < 30; i++) {
    state.fabParticles.push({
      x:      Math.random() * sz,
      y:      Math.random() * sz,
      vx:     (Math.random() - 0.5) * 0.6,
      vy:     (Math.random() - 0.5) * 0.6,
      radius: Math.random() * 1.8 + 0.6,
      phase:  Math.random() * Math.PI * 2,
    });
  }

  function draw() {
    if (!state.fabCtx || !state.fabCanvas) return;
    state.fabCtx.clearRect(0, 0, sz, sz);
    state.fabCtx.globalCompositeOperation = 'lighter';
    const t = Date.now() * 0.0025;

    for (const p of state.fabParticles) {
      const glow = 0.35 + Math.sin(t + p.phase) * 0.25;
      state.fabCtx.beginPath();
      state.fabCtx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
      state.fabCtx.fillStyle = 'rgba(0,229,255,' + glow + ')';
      state.fabCtx.fill();

      // Mouse attraction
      const dx   = state.mouseFabX - p.x;
      const dy   = state.mouseFabY - p.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 36 && dist > 0) {
        p.vx += (dx / dist) * 0.04;
        p.vy += (dy / dist) * 0.04;
      }
      p.vx *= 0.97;
      p.vy *= 0.97;
      p.x  += p.vx;
      p.y  += p.vy;
      if (p.x < 0)  p.x = sz;
      if (p.x > sz) p.x = 0;
      if (p.y < 0)  p.y = sz;
      if (p.y > sz) p.y = 0;
    }
    state.fabRafId = requestAnimationFrame(draw);
  }
  draw();

  // Mouse tracking for attraction effect
  fab.addEventListener('mousemove', e => {
    const rect     = fab.getBoundingClientRect();
    state.mouseFabX = e.clientX - rect.left;
    state.mouseFabY = e.clientY - rect.top;
  });
  fab.addEventListener('mouseleave', () => {
    state.mouseFabX = 34;
    state.mouseFabY = 34;
  });
}

/** Stops the FAB animation loop and cleans up canvas state. */
export function stopFabParticles() {
  if (state.fabRafId) {
    cancelAnimationFrame(state.fabRafId);
    state.fabRafId = null;
  }
  state.fabCanvas    = null;
  state.fabCtx       = null;
  state.fabParticles = null;
}


// ─────────────────────────────────────────────────────────────────────────────
// BRAINWAVE CANVAS ANIMATION
// ─────────────────────────────────────────────────────────────────────────────

/** Sentiment → stroke colour mapping. */
const BRAINWAVE_COLORS: Record<string, string> = {
  distress: '#FF5F7A',
  negative: '#8B5CF6',
  neutral:  '#00C5D4',
  positive: '#00CFA8',
};

/** Internal RAF handle for the brainwave loop. Null when stopped. */
let _brainwaveRafId: number | null = null;

/**
 * Starts a dual-sine brainwave animation on the supplied canvas element.
 * The wave colours are selected by `sentiment`:
 *   distress → #FF5F7A  |  negative → #8B5CF6
 *   neutral  → #00C5D4  |  positive → #00CFA8
 *
 * Wave 1: frequency coefficient 6π, no phase offset.
 * Wave 2: frequency coefficient 14π, phase offset π/3 — produces the
 *         characteristic EEG-style interference pattern.
 *
 * Safe to call while already running — cancels the previous loop first.
 *
 * @param canvas   The <canvas> element to draw into (class: ai-brainwave-canvas).
 * @param sentiment One of 'distress' | 'negative' | 'neutral' | 'positive'.
 */
export function startBrainwaveAnimation(
  canvas: HTMLCanvasElement,
  sentiment: string,
): void {
  // Cancel any existing loop before starting a new one.
  stopBrainwaveAnimation();

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const color  = BRAINWAVE_COLORS[sentiment] ?? BRAINWAVE_COLORS['neutral'];
  const freq1  = 6  * Math.PI;   // primary rhythm
  const freq2  = 14 * Math.PI;   // fast gamma overlay
  const phase2 = Math.PI / 3;    // phase shift between the two waves

  function draw(ts: number) {
    const W = canvas.width;
    const H = canvas.height;
    const t = ts * 0.001;        // convert ms → seconds for stable speed

    ctx.clearRect(0, 0, W, H);

    // Shared stroke style for both waves
    ctx.strokeStyle = color;
    ctx.lineWidth   = 1.8;
    ctx.lineCap     = 'round';

    // ── Wave 1 (primary, full opacity) ──────────────────────────────────────
    ctx.globalAlpha = 0.85;
    ctx.beginPath();
    for (let x = 0; x <= W; x++) {
      const xNorm = x / W;                          // 0 → 1
      const y = (H / 2) + (H * 0.22) *
        Math.sin(freq1 * xNorm + t * 2.4);
      x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.stroke();

    // ── Wave 2 (overtone, reduced opacity) ──────────────────────────────────
    ctx.globalAlpha = 0.42;
    ctx.lineWidth   = 1.1;
    ctx.beginPath();
    for (let x = 0; x <= W; x++) {
      const xNorm = x / W;
      const y = (H / 2) + (H * 0.13) *
        Math.sin(freq2 * xNorm + t * 3.8 + phase2);
      x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.stroke();

    ctx.globalAlpha = 1;

    _brainwaveRafId = requestAnimationFrame(draw);
  }

  _brainwaveRafId = requestAnimationFrame(draw);
}

/**
 * Cancels the active brainwave RAF loop.
 * Safe to call even if the animation is not running.
 */
export function stopBrainwaveAnimation(): void {
  if (_brainwaveRafId !== null) {
    cancelAnimationFrame(_brainwaveRafId);
    _brainwaveRafId = null;
  }
}


// ─────────────────────────────────────────────────────────────────────────────
// AMBIENT SOUNDSCAPE
// ─────────────────────────────────────────────────────────────────────────────

/** Pixabay CC0 ambient loop — soft focus/flow soundscape. */
const SOUNDSCAPE_URL =
  'https://cdn.pixabay.com/audio/2023/07/18/audio_1a4c3e6bde.mp3';

/** Internal Web Audio references, kept for clean teardown. */
let _soundCtx:    AudioContext          | null = null;
let _soundSource: AudioBufferSourceNode | null = null;

/**
 * Fetches and plays the ambient CC0 soundscape loop.
 * Creates a fresh AudioContext + GainNode on each call so the function is
 * safe to re-invoke after `stopSoundscape()`.
 *
 * Gain is fixed at 0.18 (subtle; doesn't interfere with voice dictation).
 * The source node is set to `loop = true` so it plays indefinitely.
 *
 * On successful playback start, notifies the 3D avatar module via the
 * registered `_avatarSoundscapeFn` (populated once the lazy chunk loads).
 */
export async function startSoundscape(): Promise<void> {
  // Tear down any existing soundscape first.
  stopSoundscape();

  try {
    const response = await fetch(SOUNDSCAPE_URL);
    if (!response.ok) {
      console.warn('[Kili/effects] Soundscape fetch failed:', response.status);
      return;
    }

    const arrayBuffer = await response.arrayBuffer();

    _soundCtx = new AudioContext();

    const audioBuffer = await _soundCtx.decodeAudioData(arrayBuffer);

    const gainNode         = _soundCtx.createGain();
    gainNode.gain.value    = 0.18;
    gainNode.connect(_soundCtx.destination);

    _soundSource         = _soundCtx.createBufferSource();
    _soundSource.buffer  = audioBuffer;
    _soundSource.loop    = true;
    _soundSource.connect(gainNode);
    _soundSource.start(0);

    // Notify the 3-D avatar that soundscape mode is active.
    // _avatarSoundscapeFn is null until main.ts calls registerAvatarSoundscape()
    // after the lazy chunk loads — no-op is safe here.
    _avatarSoundscapeFn?.(true);
  } catch (err) {
    console.error('[Kili/effects] startSoundscape error:', err);
    // Ensure we don't leave a half-open context.
    stopSoundscape();
  }
}

/**
 * Stops the ambient soundscape immediately and releases all Web Audio
 * resources.  Notifies the avatar module to revert its animation state.
 *
 * Safe to call even if no soundscape is currently playing.
 */
export function stopSoundscape(): void {
  try {
    if (_soundSource) {
      _soundSource.stop();
      _soundSource.disconnect();
      _soundSource = null;
    }
  } catch (_) {
    // stop() throws if the source was never started; suppress silently.
  }

  try {
    if (_soundCtx) {
      _soundCtx.close();
      _soundCtx = null;
    }
  } catch (_) {}

  // Notify the avatar module regardless of teardown errors.
  _avatarSoundscapeFn?.(false);
}
